import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router";
import { useAuth } from "@itemgo/core";
import { NotificationBell } from "@itemgo/messaging";
import CommandPalette from "@/components/CommandPalette";
import {
  LayoutDashboard, Users, BookOpen, Puzzle, CreditCard, DollarSign,
  Briefcase, TrendingUp, FileText, BadgePercent, Settings as SettingsIcon,
  Ticket, Bell, Bug, Menu, X, ChevronDown, LogOut, ClipboardList,
  Megaphone, Wrench, MessageSquare, Mail, MapPin, Map, Trash2, Truck,
} from "lucide-react";

/* ─── Page title lookup ─────────────────────────────────── */

const PAGE_TITLES: Record<string, string> = {
  "/": "Dashboard",
  "/users": "Users",
  "/bookings": "Bookings",
  "/push": "Push Campaign",
  "/plugins": "Plugins",
  "/ads": "Ad Manager",
  "/inbox": "Inbox",
  "/tickets": "Tickets",
  "/messages": "Chat Monitor",
  "/contact": "Contact Messages",
  "/zones": "Zones",
  "/drivers-map": "Driver Map",
  "/fleet": "Fleet",
  "/settings": "Platform Settings",
  "/maintenance": "Maintenance",
  "/accounting": "Financial Dashboard",
  "/accounting/pricing": "Pricing Manager",
  "/accounting/invoices": "Invoices",
  "/accounting/vat-profiles": "VAT Profiles",
  "/subscribers": "Subscribers",
  "/payouts": "Payouts",
  "/leads": "Waitlist Leads",
  "/errors": "Client Errors",
  "/jobs": "Job Analytics",
};

function getPageTitle(pathname: string): string {
  // Try exact match first
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];

  // Try dynamic-route pattern matches (longest prefix wins)
  if (pathname.startsWith("/users/")) return "User Profile";
  if (pathname.startsWith("/bookings/")) return "Booking Details";

  // Try prefix match against known base paths
  const sortedKeys = Object.keys(PAGE_TITLES).sort((a, b) => b.length - a.length);
  for (const key of sortedKeys) {
    if (key !== "/" && pathname.startsWith(key)) {
      return PAGE_TITLES[key];
    }
  }

  return "ItemGo Admin";
}

interface NavGroup {
  label: string;
  items: { label: string; path: string; icon: React.ReactNode }[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: "Overview",
    items: [
      { label: "Dashboard", path: "/", icon: <LayoutDashboard size={18} /> },
      { label: "Inbox", path: "/inbox", icon: <Bell size={18} /> },
    ],
  },
  {
    label: "Users",
    items: [
      { label: "All Users", path: "/users", icon: <Users size={18} /> },
      { label: "Waitlist Leads", path: "/leads", icon: <ClipboardList size={18} /> },
    ],
  },
  {
    label: "Operations",
    items: [
      { label: "Bookings", path: "/bookings", icon: <BookOpen size={18} /> },
      { label: "Plugins", path: "/plugins", icon: <Puzzle size={18} /> },
      { label: "Push Campaign", path: "/push", icon: <Megaphone size={18} /> },
      { label: "Email Campaign", path: "/email", icon: <Mail size={18} /> },
      { label: "Deletion Requests", path: "/deletions", icon: <Trash2 size={18} /> },
      { label: "Ads", path: "/ads", icon: <TrendingUp size={18} /> },
      { label: "Zones", path: "/zones", icon: <MapPin size={18} /> },
      { label: "Driver Map", path: "/drivers-map", icon: <Map size={18} /> },
      { label: "Fleet", path: "/fleet", icon: <Truck size={18} /> },
    ],
  },
  {
    label: "Business",
    items: [
      { label: "Subscribers", path: "/subscribers", icon: <CreditCard size={18} /> },
      { label: "Payouts", path: "/payouts", icon: <DollarSign size={18} /> },
      { label: "Jobs", path: "/jobs", icon: <Briefcase size={18} /> },
    ],
  },
  {
    label: "Finance",
    items: [
      { label: "Dashboard", path: "/accounting", icon: <TrendingUp size={18} /> },
      { label: "Pricing Manager", path: "/accounting/pricing", icon: <BadgePercent size={18} /> },
      { label: "Invoices", path: "/accounting/invoices", icon: <FileText size={18} /> },
      { label: "VAT Profiles", path: "/accounting/vat-profiles", icon: <FileText size={18} /> },
    ],
  },
  {
    label: "Support",
    items: [
      { label: "Tickets", path: "/tickets", icon: <Ticket size={18} /> },
      { label: "Push Campaign", path: "/push", icon: <Bell size={18} /> },
      { label: "Chat Monitor", path: "/messages", icon: <MessageSquare size={18} /> },
      { label: "Contact Messages", path: "/contact", icon: <Mail size={18} /> },
      { label: "Client Errors", path: "/errors", icon: <Bug size={18} /> },
    ],
  },
  {
    label: "Settings",
    items: [
      { label: "Platform", path: "/settings", icon: <SettingsIcon size={18} /> },
      { label: "Maintenance", path: "/maintenance", icon: <Wrench size={18} /> },
    ],
  },
];

const MOBILE_NAV = [
  { label: "Dashboard", path: "/", icon: <LayoutDashboard size={20} /> },
  { label: "Users", path: "/users", icon: <Users size={20} /> },
  { label: "Bookings", path: "/bookings", icon: <BookOpen size={20} /> },
  { label: "Finance", path: "/accounting", icon: <TrendingUp size={20} /> },
  { label: "More", path: "", icon: <Menu size={20} /> },
];

/* ─── Sidebar ─────────────────────────────────────────── */

function Sidebar({ onClose }: { onClose?: () => void }) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const groups: Record<string, boolean> = {};
    for (const group of NAV_GROUPS) {
      if (group.items.some((item) => {
        if (item.path === "/") return location.pathname === "/";
        return location.pathname.startsWith(item.path);
      })) {
        groups[group.label] = true;
      }
    }
    return groups;
  });

  return (
    <aside className="w-64 h-screen bg-white border-r border-surface-border flex flex-col shrink-0 overflow-y-auto">
      <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/logo.svg" alt="ItemGo" className="h-8 w-auto" />
          <span className="text-xs font-medium text-brand-orange bg-brand-orange/10 px-2 py-0.5 rounded-full">
            Admin
          </span>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1 hover:bg-surface-muted rounded-lg">
            <X size={16} className="text-text-secondary" />
          </button>
        )}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        {NAV_GROUPS.map((group) => {
          const expanded = expandedGroups[group.label] ?? false;
          return (
            <div key={group.label}>
              <button
                onClick={() => setExpandedGroups((p) => ({ ...p, [group.label]: !p[group.label] }))}
                className="w-full flex items-center justify-between px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary hover:text-text-secondary transition-colors"
              >
                {group.label}
                <ChevronDown size={12} className={`transition-transform ${expanded ? "rotate-0" : "-rotate-90"}`} />
              </button>
              {expanded && (
                <div className="ml-1 space-y-0.5 mt-0.5">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={item.path === "/"}
                      onClick={onClose}
                      className={({ isActive: active }) =>
                        `flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition-colors ${
                          active
                            ? "bg-brand-blue text-white font-medium shadow-sm"
                            : "text-text-secondary hover:bg-surface-muted hover:text-text-primary"
                        }`
                      }
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="px-3 py-3 border-t border-surface-border">
        <div className="flex items-center gap-2 px-3 py-2">
          <div className="w-7 h-7 rounded-full bg-brand-blue/10 flex items-center justify-center text-xs font-bold text-brand-blue shrink-0">
            {user?.displayName?.charAt(0) || "A"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-text-primary truncate">{user?.displayName || "Admin"}</p>
            <p className="text-[10px] text-text-tertiary truncate">{user?.email}</p>
          </div>
          <button onClick={() => signOut()} className="p-1.5 hover:bg-surface-muted rounded-lg" title="Sign out">
            <LogOut size={14} className="text-text-tertiary" />
          </button>
        </div>
      </div>
    </aside>
  );
}

/* ─── Mobile bottom nav ──────────────────────────────── */

function MobileBottomNav({ onOpenSidebar }: { onOpenSidebar: () => void }) {
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-surface-border flex items-center justify-around px-2 pb-safe">
      {MOBILE_NAV.map((item) => {
        if (item.path === "") {
          return (
            <button key="more" onClick={onOpenSidebar}
              className="flex flex-col items-center gap-0.5 py-2 px-3 min-w-[60px] text-text-secondary"
            >
              {item.icon}
              <span className="text-[9px] font-medium">{item.label}</span>
            </button>
          );
        }
        return (
          <NavLink key={item.path} to={item.path} end={item.path === "/"}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 py-2 px-3 min-w-[60px] ${
                isActive ? "text-brand-blue" : "text-text-secondary"
              }`
            }
          >
            {item.icon}
            <span className="text-[9px] font-medium">{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

/* ─── Desktop top bar ──────────────────────────────────── */

function DesktopTopBar() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const pageTitle = getPageTitle(location.pathname);
  const initial = user?.displayName?.charAt(0) || "A";

  return (
    <header className="hidden lg:flex sticky top-0 z-40 bg-white border-b border-surface-border h-14 items-center justify-between px-6">
      <h1 className="text-base font-semibold text-text-primary truncate">{pageTitle}</h1>

      <div className="flex items-center gap-3">
        <NotificationBell />

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((p) => !p)}
            className="flex items-center gap-2 p-1 pr-2 rounded-full hover:bg-surface-muted transition-colors"
          >
            <div className="w-7 h-7 rounded-full bg-brand-blue/10 flex items-center justify-center text-xs font-bold text-brand-blue shrink-0">
              {initial}
            </div>
            <ChevronDown size={14} className="text-text-secondary" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white border border-surface-border rounded-xl shadow-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-surface-border">
                <p className="text-sm font-medium text-text-primary truncate">{user?.displayName || "Admin"}</p>
                <p className="text-xs text-text-secondary truncate">{user?.email}</p>
              </div>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  signOut();
                }}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-text-secondary hover:bg-surface-muted hover:text-text-primary transition-colors"
              >
                <LogOut size={14} />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

/* ─── Main layout ────────────────────────────────────── */

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Global Cmd/Ctrl+K command palette.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((p) => !p);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen bg-surface-bg flex">
      <div className="hidden lg:block">
        <Sidebar />
      </div>

      {mobileSidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileSidebarOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0">
            <Sidebar onClose={() => setMobileSidebarOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-h-screen pb-16 lg:pb-0">
        <DesktopTopBar />

        <header className="lg:hidden bg-white border-b border-surface-border px-4 py-3 flex items-center justify-between sticky top-0 z-40">
          <button onClick={() => setMobileSidebarOpen(true)} className="p-1.5 hover:bg-surface-muted rounded-lg">
            <Menu size={20} className="text-text-primary" />
          </button>
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="ItemGo" className="h-7 w-auto" />
          </div>
          <NotificationBell />
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto w-full">
          {children}
        </main>
      </div>

      <div className="lg:hidden">
        <MobileBottomNav onOpenSidebar={() => setMobileSidebarOpen(true)} />
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
